import { CSSProperties, useMemo } from 'react';

export type HeatmapFeature =
  | 'playground'
  | 'themes'
  | 'intensity'
  | 'layout'
  | 'split-month'
  | 'locales'
  | 'filter'
  | 'cells'
  | 'sheet'
  | 'weighted'
  | 'navigate';

export type ActivityHeatmapEmbedProps = {
  /** Folder that contains the Flutter `index.html`. */
  assetBase?: string;
  /** Catalog id, same as `?feature=` */
  feature?: HeatmapFeature | string;
  theme?: 'light' | 'dark' | 'system';
  /** BCP 47 tag, e.g. `zh-Hant`, `en`, `ja` */
  locale?: string;
  /** Hide tabs / knobs and show only the heatmap */
  chrome?: boolean;
  /** CSS height of the wrapping div. Default 680 */
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  title?: string;
};

/**
 * Wraps the Flutter web build in a `div`. Copy `build/web` to
 * `public/open-source/activity-heatmap-calendar/` and point [assetBase] at it.
 *
 * ```tsx
 * <ActivityHeatmapEmbed
 *   assetBase="/open-source/activity-heatmap-calendar/"
 *   feature="playground"
 *   locale="zh-Hant"
 * />
 * ```
 */
export function ActivityHeatmapEmbed({
  assetBase = '/open-source/activity-heatmap-calendar/',
  feature = 'playground',
  theme,
  locale,
  chrome = true,
  height = 680,
  className,
  style,
  title = 'Activity heatmap calendar',
}: ActivityHeatmapEmbedProps) {
  const src = useMemo(() => {
    const base = assetBase.endsWith('/') ? assetBase : `${assetBase}/`;
    const params = new URLSearchParams();
    if (feature) params.set('feature', feature);
    if (theme) params.set('theme', theme);
    if (locale) params.set('locale', locale);
    if (!chrome) params.set('chrome', '0');
    const search = params.toString();
    return `${base}index.html${search ? `?${search}` : ''}`;
  }, [assetBase, feature, theme, locale, chrome]);

  return (
    <div
      className={className}
      style={{ width: '100%', height, overflow: 'hidden', ...style }}
    >
      <iframe
        src={src}
        title={title}
        loading="lazy"
        style={{
          width: '100%',
          height: '100%',
          border: 0,
          display: 'block',
          background: 'transparent',
        }}
      />
    </div>
  );
}

export default ActivityHeatmapEmbed;
